import type { ReactNode } from "react";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { cn } from "@/lib/utils";

/**
 * The layout pieces every organiser screen is built from.
 *
 * Two things here are rules rather than convenience:
 *
 * - `adm:` is the admin's own breakpoint (960px, see theme.css). Below it the
 *   shell is tab chips and tappable rows; above it, a sidebar and tables. The
 *   outgoing system needed `.adm-mobile-only` / `.adm-desktop-only` classes and
 *   a warning never to put them on a button, because a single-class selector
 *   lost the cascade to `.adm-btn`. Tailwind variants have no such trap:
 *   `hidden adm:flex` on a button does exactly what it says.
 *
 * - A panel is mounted ONCE and both branches render from it. Rendering the
 *   panel twice puts dialogs inside a `display:none` subtree, which once made
 *   desktop editing silently impossible.
 */

/** The page header row: title, one line of context, actions. Works at both
 *  breakpoints, so a panel never needs two of them. */
export function PageHeader({
  title,
  description,
  actions,
  className,
}: {
  title: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  className?: string | undefined;
}) {
  return (
    <div
      className={cn(
        "flex flex-col gap-3 pb-4 adm:flex-row adm:items-end adm:justify-between",
        className,
      )}
    >
      <div className="min-w-0 space-y-1">
        <h1 className="font-heading text-xl leading-tight font-semibold tracking-tight text-balance">
          {title}
        </h1>
        {description !== undefined && (
          <p className="text-sm text-muted-foreground text-pretty">{description}</p>
        )}
      </div>
      {actions !== undefined && (
        <div className="flex shrink-0 flex-wrap items-center gap-2">{actions}</div>
      )}
    </div>
  );
}

/** A titled block inside a panel. The label is the old `sk-label`. */
export function Section({
  label,
  hint,
  actions,
  children,
  className,
}: {
  label?: ReactNode;
  hint?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
  className?: string | undefined;
}) {
  return (
    <section className={cn("space-y-2", className)}>
      {(label !== undefined || actions !== undefined) && (
        <div className="flex items-baseline justify-between gap-3">
          {label !== undefined && <SectionLabel>{label}</SectionLabel>}
          {actions}
        </div>
      )}
      {hint !== undefined && <p className="text-sm text-muted-foreground text-pretty">{hint}</p>}
      {children}
    </section>
  );
}

/** Uppercase field/section label. Short, and always a fixed English string or
 *  a count, never a sentence, because tracking this wide is hard to read. */
export function SectionLabel({
  children,
  className,
}: {
  children: ReactNode;
  className?: string | undefined;
}) {
  return (
    <span
      className={cn(
        "block text-xs font-semibold tracking-wide text-muted-foreground uppercase",
        className,
      )}
    >
      {children}
    </span>
  );
}

/**
 * The sub-view switcher inside a tab (Spirit's four views, Stats' six).
 *
 * Real `Tabs`, not buttons pretending to be tabs: it gets the roving focus,
 * the arrow-key navigation and the active treatment for free, and it looks
 * the same everywhere it appears.
 *
 * A sub-view name must not collide with a top-level tab name: both rows stack
 * on a phone, so a "Teams" tab under Stats sent organisers to the roster
 * editor. It is "Team profiles" for that reason alone.
 */
export function SubTabs<T extends string>({
  views,
  active,
  onSelect,
  className,
  listClassName,
}: {
  views: { id: T; label: string; count?: number | undefined }[];
  active: T;
  onSelect: (id: T) => void;
  className?: string | undefined;
  /** The pill list itself: `w-auto` where it should hug its tabs rather
   *  than take the row (the board's laptop header). */
  listClassName?: string | undefined;
}) {
  return (
    <Tabs
      value={active}
      onValueChange={(next) => {
        if (typeof next === "string") onSelect(next as T);
      }}
      className={cn("mb-4", className)}
    >
      <TabsList className={listClassName}>
        {views.map((v) => (
          <TabsTrigger key={v.id} value={v.id}>
            {v.label}
            {v.count !== undefined && v.count > 0 && (
              <span className="tabular text-xs opacity-60">{v.count}</span>
            )}
          </TabsTrigger>
        ))}
      </TabsList>
    </Tabs>
  );
}

/** Quiet supporting text. Wraps, unlike a row's own description line. */
export function Hint({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <p className={cn("text-sm leading-relaxed text-muted-foreground text-pretty", className)}>
      {children}
    </p>
  );
}

/** Nothing to show. Never a blank area: say what would appear here. */
export function EmptyState({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div
      className={cn(
        "rounded-xl border border-dashed bg-card/50 px-4 py-8 text-center text-sm text-muted-foreground text-pretty",
        className,
      )}
    >
      {children}
    </div>
  );
}

/**
 * An action failed, or a form is not valid yet.
 *
 * Red is semantic here ("this did not happen"), and it is the only place red
 * appears outside a stop/destroy control. Where the SERVER refused, show
 * `ApiError.message`: it carries the real sentence ("3 game(s) still reference
 * this team"), and inventing a friendlier one loses the reason.
 */
export function FormError({ children, className }: { children: ReactNode; className?: string }) {
  if (children === null || children === undefined || children === false) return null;
  return (
    <div
      role="alert"
      className={cn(
        "rounded-lg border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm font-medium text-destructive text-pretty",
        className,
      )}
    >
      {children}
    </div>
  );
}

/**
 * The dark accent strip: `--band`, the app's one dark surface on a light
 * page. Page headers on the phone surfaces, the sidebar brand. Never a page
 * ground. (The door pages wore it as a card head until the board redesign
 * put them on the dark ground: `components/app/gate.tsx`.)
 */
export function Band({
  eyebrow,
  title,
  children,
  className,
}: {
  eyebrow?: ReactNode;
  title?: ReactNode;
  children?: ReactNode;
  className?: string | undefined;
}) {
  return (
    <div
      className={cn(
        "flex items-center justify-between gap-4 bg-band px-4 py-3 text-band-foreground",
        className,
      )}
    >
      {(eyebrow !== undefined || title !== undefined) && (
        <div className="min-w-0 space-y-1.5">
          {eyebrow !== undefined && (
            <span className="eyebrow block text-band-muted">{eyebrow}</span>
          )}
          {title !== undefined && (
            <span className="nxt block truncate text-xl leading-none">{title}</span>
          )}
        </div>
      )}
      {children}
    </div>
  );
}
